// apps/patient/src/components/messages/ThreadListItem.tsx

import { formatDistanceToNow } from 'date-fns';
import {
  User,
  Paperclip,
  Bell
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { usePatient } from '../../contexts/PatientContext';

export default function ThreadListItem({
  thread,
  isSelected,
  isUnread,
  onSelect,
  onMarkRead,
  getPriorityColor,
  getCategoryColor,
  getStatusIcon
}) {
  const { patient } = usePatient();
  const isSentByMe = thread.last_sender_id === patient?.id;

  return (
    <div
      onClick={onSelect}
      className={cn(
        "px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors",
        isSelected && "bg-primary/5 hover:bg-primary/10",
        isUnread && "bg-blue-50/50"
      )}
    >
      <div className="flex items-start space-x-3">
        {/* Avatar */}
        <div className="flex-shrink-0">
          <div className={cn(
            "h-10 w-10 rounded-full flex items-center justify-center",
            isUnread ? "bg-primary text-white" : "bg-gray-200 text-gray-600"
          )}>
            <User className="h-5 w-5" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header Row */}
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className={cn(
                  "text-sm truncate",
                  isUnread ? "font-semibold text-gray-900" : "font-medium text-gray-900"
                )}>
                  {thread.subject || 'No subject'}
                </p>

                {/* Priority Badge - Read only for patients */}
                {thread.priority === 'urgent' && (
                  <span className={cn(
                    "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border",
                    getPriorityColor(thread.priority)
                  )}>
                    Urgent
                  </span>
                )}

                {/* Status Icon */}
                {getStatusIcon(thread.status)}
              </div>

              {/* Last sender info */}
              <p className="text-xs text-gray-500 mt-0.5">
                {isSentByMe
                  ? `To: ${thread.last_sender_name !== thread.patient_name ? thread.patient_name || 'Provider' : 'Provider'}`
                  : `From: ${thread.last_sender_name || 'Provider'}`
                }
              </p>
            </div>

            {/* Time */}
            <span className={cn(
              "text-xs whitespace-nowrap ml-2",
              isUnread ? "font-semibold text-gray-900" : "text-gray-500"
            )}>
              {formatDistanceToNow(new Date(thread.last_message_at), { addSuffix: true })}
            </span>
          </div>

          {/* Message Preview */}
          <p className={cn(
            "text-sm mt-1 line-clamp-2",
            isUnread ? "text-gray-900" : "text-gray-600"
          )}>
            {thread.last_message_preview || 'No messages yet'}
          </p>

          {/* Footer */}
          <div className="flex items-center gap-3 mt-2">
            {/* Category */}
            <span className={cn(
              "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
              getCategoryColor(thread.category)
            )}>
              {thread.category?.replace('_', ' ')}
            </span>

            {/* Indicators */}
            <div className="flex items-center gap-2 text-gray-400">
              {thread.contains_attachments && (
                <Paperclip className="h-3 w-3" />
              )}
              {thread.requires_action && (
                <Bell className="h-3 w-3 text-yellow-500" />
              )}
            </div>

            {/* Unread indicator */}
            {isUnread && (
              <span className="ml-auto inline-flex items-center justify-center px-2 py-0.5 bg-primary text-white text-xs font-medium rounded-full">
                NEW
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}